import type React from 'react';
import { type FC } from 'react';
import { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ModalConfig, ModalContextValue, ModalProviderProps, ModalState } from './types';
import ModalPortal from './components/ModalPortal';
import { MODAL_CONFIGS } from './registry';
import { noop } from 'lodash';
import { useUrlModalTrigger } from './hooks/useUrlModalTrigger';

const defaultContextValue: ModalContextValue = {
  activeModals: [],
  openModal: () => '',
  closeModal: noop,
  closeAllModals: noop,
  closeModalByType: noop,
  isModalOpen: () => false,
  getModalById: () => undefined,
};

export const ModalContext = createContext<ModalContextValue>(defaultContextValue);

// Generate unique modal IDs using timestamp and random component to avoid SSR conflicts
const generateModalId = (): string => {
  // Timestamp + random number for collision resistance without global state
  return `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Action types for reducer
type ModalAction =
  | { type: 'OPEN_MODAL'; payload: ModalConfig }
  | { type: 'CLOSE_MODAL'; id: string }
  | { type: 'CLOSE_ALL_MODALS' }
  | { type: 'CLOSE_MODALS_BY_TYPE'; modalType: string };

// Reducer for modal state management
const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        activeModals: [...state.activeModals, action.payload].sort((a, b) => (b.priority ?? 1) - (a.priority ?? 1)),
      };

    case 'CLOSE_MODAL':
      return {
        activeModals: state.activeModals.filter((modal) => modal.id !== action.id),
      };

    case 'CLOSE_ALL_MODALS':
      return {
        activeModals: [],
      };

    case 'CLOSE_MODALS_BY_TYPE':
      return {
        activeModals: state.activeModals.filter((modal) => modal.type !== action.modalType),
      };

    default:
      return state;
  }
};

// Separate component for SSR-safe portal rendering
// Portals require DOM elements which don't exist during server-side rendering
// This wrapper prevents hydration mismatches by only rendering portals on the client
const ModalPortalWrapper: FC = () => {
  const [hasMounted, setHasMounted] = useState(false);

  // Ensure we only render portals after client-side hydration
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Return null during SSR and initial client render to prevent hydration mismatch
  if (!hasMounted) {
    return null;
  }

  return <ModalPortal />;
};

export const ModalProvider: FC<ModalProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(modalReducer, { activeModals: [] });

  // Get real listing data from Redux for URL-triggered modals - memoized selectors
  const currentListing = useSelector(
    (state: any) => state.currentListingDetails?.currentListing,
    // Shallow equality check to prevent unnecessary re-renders
    (prev, next) => prev === next,
  );
  const similarListings = useSelector(
    (state: any) => state.fetchListing?.similarListings,
    // Shallow equality check to prevent unnecessary re-renders
    (prev, next) => prev === next,
  );
  const userExists = useSelector(
    (state: any) => state.app?.userExists,
    // Shallow equality check to prevent unnecessary re-renders
    (prev, next) => prev === next,
  );

  // Use ref to track the original body overflow value
  const originalOverflowRef = useRef<string>('');
  const hasStoredOverflowRef = useRef<boolean>(false);

  // Ref to access current state in callbacks without causing re-renders
  const stateRef = useRef(state);
  stateRef.current = state;

  // Centralized ESC key handling - only one listener for all modals
  const handleGlobalKeydown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      // Close the highest priority (first) modal that can handle ESC
      const highestPriorityModal = stateRef.current.activeModals[0];
      if (highestPriorityModal) {
        // Check if this is a webview modal - webview modals should not be closed by ESC
        let isWebviewModal = highestPriorityModal.props?.isWebview;

        // Fallback: Check URL params for webview modal (for legacy system support)
        if (!isWebviewModal && typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          isWebviewModal = urlParams.has('webviewModal');
        }

        // Skip ESC handling for webview modals
        if (isWebviewModal) {
          return;
        }

        dispatch({ type: 'CLOSE_MODAL', id: highestPriorityModal.id });
        // Execute callback after state update
        highestPriorityModal.onClose?.();
      }
    }
  }, []);

  // Set up global ESC key listener
  useEffect(() => {
    // Only add listener if there are active modals
    if (state.activeModals.length > 0) {
      document.addEventListener('keydown', handleGlobalKeydown);
      return () => {
        document.removeEventListener('keydown', handleGlobalKeydown);
      };
    }
  }, [state.activeModals.length, handleGlobalKeydown]);

  // Optimized body scroll lock - only update when modal count crosses the 0/1 boundary
  const previousModalCountRef = useRef(0);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const currentModalCount = state.activeModals.length;
    const previousModalCount = previousModalCountRef.current;

    // Only update DOM when crossing the 0/1 boundary to prevent thrashing
    if (previousModalCount === 0 && currentModalCount > 0) {
      // First modal opened - lock scroll
      originalOverflowRef.current = document.body.style.overflow || '';
      hasStoredOverflowRef.current = true;
      document.body.style.overflow = 'hidden';
    } else if (previousModalCount > 0 && currentModalCount === 0) {
      // Last modal closed - restore scroll
      document.body.style.overflow = originalOverflowRef.current;
      hasStoredOverflowRef.current = false;
      originalOverflowRef.current = '';
    }

    // Update ref for next comparison
    previousModalCountRef.current = currentModalCount;

    // Cleanup function to ensure body overflow is restored on unmount
    return () => {
      if (hasStoredOverflowRef.current && typeof document !== 'undefined') {
        document.body.style.overflow = originalOverflowRef.current;
        hasStoredOverflowRef.current = false;
        originalOverflowRef.current = '';
      }
    };
  }, [state.activeModals.length]);

  const openModal = useCallback((config: Omit<ModalConfig, 'id'>): string => {
    const id = generateModalId();

    // Merge registry default props with passed props
    const registryConfig = MODAL_CONFIGS[config.type as keyof typeof MODAL_CONFIGS];
    const mergedProps = registryConfig?.defaultProps
      ? { ...registryConfig.defaultProps, ...config.props }
      : config.props;

    const newModal: ModalConfig = {
      ...config,
      id,
      priority: config.priority ?? 1,
      props: mergedProps,
    };

    dispatch({ type: 'OPEN_MODAL', payload: newModal });

    // Call onOpen callback if provided (useful for analytics)
    config.onOpen?.();

    return id;
  }, []);

  const closeModal = useCallback((id: string): void => {
    // Store modal reference before dispatch to avoid stale closure
    const modal = stateRef.current.activeModals.find((m) => m.id === id);
    dispatch({ type: 'CLOSE_MODAL', id });

    // Execute callback after state update
    modal?.onClose?.();
  }, []);

  const closeAllModals = useCallback((): void => {
    const callbacks = stateRef.current.activeModals.filter((modal) => modal.onClose).map((modal) => modal.onClose!);

    dispatch({ type: 'CLOSE_ALL_MODALS' });

    // Execute callbacks after state update
    callbacks.forEach((callback) => callback());
  }, []);

  const closeModalByType = useCallback((type: string): void => {
    const callbacks = stateRef.current.activeModals
      .filter((modal) => modal.type === type && modal.onClose)
      .map((modal) => modal.onClose!);

    dispatch({ type: 'CLOSE_MODALS_BY_TYPE', modalType: type });

    // Execute callbacks after state update
    callbacks.forEach((callback) => callback());
  }, []);

  const isModalOpen = useCallback(
    (type: string): boolean => {
      return state.activeModals.some((modal) => modal.type === type);
    },
    [state.activeModals],
  );

  const getModalById = useCallback(
    (id: string): ModalConfig | undefined => {
      return state.activeModals.find((modal) => modal.id === id);
    },
    [state.activeModals],
  );

  // Create stable additional props for URL modal triggering
  const urlModalProps = useMemo(() => {
    return currentListing && currentListing.contact
      ? {
          listing: currentListing,
          similarListings: similarListings || [],
          isPopup: true,
          userExists: userExists || false,
        }
      : {
          // Fallback when no listing is loaded (e.g., testing modals via URL params)
          // Modal components will read additional data from Redux as needed
          isPopup: true,
          userExists: userExists || false,
        };
  }, [currentListing, similarListings, userExists]);

  // Enable URL-based modal triggering with real listing data
  useUrlModalTrigger(
    true, // Enable the hook
    urlModalProps,
    {
      openModal,
      closeModalByType,
      location: typeof window !== 'undefined' ? window.location : undefined,
    },
  );

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo<ModalContextValue>(
    () => ({
      activeModals: state.activeModals,
      openModal,
      closeModal,
      closeAllModals,
      closeModalByType,
      isModalOpen,
      getModalById,
    }),
    [state.activeModals, openModal, closeModal, closeAllModals, closeModalByType, isModalOpen, getModalById],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <ModalPortalWrapper />
    </ModalContext.Provider>
  );
};

export default ModalProvider;
